<script setup lang="ts">
// 2026-10-02 (paolo): the Exec screen is household-wide, not fund-specific —
// the same three cards on every fund page (and on localhost): the dealing
// plan, Auto-Execute (one per-user ON/OFF; the server stores one config row),
// and Signal at Decision Time with one row per fund the caller may access.
// The BGA-only execution-status line is gone: the dealing plan above already
// shows every fund's leg outcomes. The Strategy Allocation card moved to
// Strategy → Allocation.
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { api, type UserExecutionConfig, type OtpResidualStatus, type ExecFundDecision } from '../api/client'
import { useAuth } from '../composables/useAuth'
import DealingPlanCard from './DealingPlanCard.vue'

// Linked users Task 9 (spec 2026-09-28): a viewer (read-only account) never
// sees the Auto-Execute toggle — the server enforces this
// anyway (Task 4); this only avoids a dead control. Shown as a read-only
// status line instead.
const { isViewer } = useAuth()

const config = ref<UserExecutionConfig>({ userId: '', autoExecute: false, maxRetries: 3, compoundOtp: true })
const showExecInfo = ref(false)
const loading = ref(true)
// True only once the config GET has succeeded; the OFF note must not show on the default (unloaded) config.
const configLoaded = ref(false)
const error = ref<string | null>(null)
const saved = ref(false)
let savedTimeout: ReturnType<typeof setTimeout> | null = null

// Per-fund decisions (GET /api/user/exec/decisions), refreshed each minute so
// the morning's decisions appear without a reload.
const decisions = ref<ExecFundDecision[]>([])
let decisionsTimer: ReturnType<typeof setInterval> | null = null

const FUND_LABEL: Record<ExecFundDecision['fund'], string> = {
  BGA: 'BGA', JH: 'JH', GOLD: 'Gold', GGE: 'GGE', JAPAN: 'Japan', CHARTERIS: 'Charteris', AMATI: 'Amati',
  MINERS: 'Miners',
  JAPANESG: 'Japan ESG',
  TECH: 'Tech',
  ELECTRUM: 'Electrum',
  JHTECH: 'JHTech',
  ASIATECH: 'AsiaTech',
  BARINGSOIL: 'BaringsOil',
  SPWORLD: 'SPWorld',
  SMALLCAP: 'SmallCap',
}

async function fetchDecisions() {
  try {
    decisions.value = await api.execDecisions()
  } catch { /* keep the last good rows */ }
}

// Half-up rounding as the value is written: toFixed works on the binary
// float, so 10.45 (stored as 10.4499…) became "10.4" (2026-10-02, Gold).
// Snapping the scaled value to 12 significant digits first removes that
// representation error before rounding.
function roundHalfUp(v: number, dp: number): string {
  const f = 10 ** dp
  return (Math.round(Number((v * f).toPrecision(12))) / f).toFixed(dp)
}

function formatSignal(d: ExecFundDecision): string {
  if (d.signalValue == null || d.signalUnit == null) return '—'
  const sign = d.signalValue > 0 ? '+' : d.signalValue < 0 ? '−' : ''
  const abs = Math.abs(d.signalValue)
  const body = d.signalUnit === 'bp' ? `${roundHalfUp(abs, 1)}bp` : `${roundHalfUp(abs, 2)}%`
  return d.note ? `${sign}${body} ${d.note}` : `${sign}${body}`
}

function formatTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' })
}

// OTP/BGA divorce Phase 1, Task 4: replaces the retired OTP Reserve/Account
// dropdowns — OTP sizing is now residual-funded (post-cutoff leftover cash),
// not an operator-set reserve/account pair. Read-only.
const otpResidual = ref<OtpResidualStatus | null>(null)

async function fetchOtpResidual() {
  try {
    otpResidual.value = await api.otpResidualStatus()
  } catch { /* not available */ }
}

const otpResidualLine = computed(() => {
  const byAccount = otpResidual.value?.byAccount ?? []
  if (byAccount.length === 0) return '—'
  return byAccount
    .map(a => `${a.accountId}: £${Math.round(a.residualGbp).toLocaleString('en-GB')}`)
    .join(' · ')
})

async function loadData() {
  loading.value = true
  error.value = null
  try {
    config.value = await api.userExecutionConfig()
    configLoaded.value = true
  } catch (e: any) {
    error.value = e.message
  } finally {
    loading.value = false
  }
}

function buildExecConfigRequest() {
  return {
    autoExecute: config.value.autoExecute,
    maxRetries: config.value.maxRetries ?? 3,
    // compoundOtp deliberately omitted: Auto-compound is owned by the
    // Account panel (2026-09-27); omitting it keeps the stored value.
  }
}

async function toggleAutoExecute() {
  error.value = null
  try {
    await api.userSaveExecutionConfig(buildExecConfigRequest())
    saved.value = true
    if (savedTimeout) clearTimeout(savedTimeout)
    savedTimeout = setTimeout(() => { saved.value = false }, 2000)
  } catch (e: any) {
    // The save was refused: put the switch back so it matches the server.
    config.value.autoExecute = !config.value.autoExecute
    error.value = e.message
  }
}

onMounted(() => {
  loadData()
  fetchOtpResidual()
  fetchDecisions()
  decisionsTimer = setInterval(fetchDecisions, 60_000)
})

onUnmounted(() => {
  if (decisionsTimer) { clearInterval(decisionsTimer); decisionsTimer = null }
  if (savedTimeout) { clearTimeout(savedTimeout); savedTimeout = null }
})
</script>

<template>
  <section class="exec-panel">
    <!-- Target-weight rebalancing plan Task 5: household-wide dealing plan,
         mounted for every portfolio subdomain (not fund-scoped). -->
    <DealingPlanCard />

    <!-- The Strategy Allocation card moved to Strategy → Allocation
         (2026-10-02, paolo). -->

    <!-- Auto-Execute Card (household-wide: rendered on every fund page) -->
    <div class="card" data-testid="auto-execute-card">
      <h3>Auto-Execute <span class="info-icon" @click.stop="showExecInfo = !showExecInfo">i</span></h3>

      <div v-if="showExecInfo" class="info-panel">
        <p><strong>Auto-Execute</strong> is one switch for your whole portfolio: every fund in your dealing plan (Strategy &rarr; Allocation).</p>

        <p><strong>When ON:</strong></p>
        <ul>
          <li>Blue Box executes the day's <strong>dealing plan</strong> as soon as it is published (<strong>08:40 UK</strong>, before HL's 09:00 fund dealing cutoff)</li>
          <li>There is no time to choose &mdash; auto-execute is simply ON or OFF</li>
          <li>The plan decides <strong>BUY</strong>, <strong>SELL</strong> or <strong>HOLD</strong> for each fund; Blue Box places every fund's plan orders on HL across your allocated accounts</li>
          <li>If you're already in the correct position (e.g. the plan says BUY Miners and you already hold it), no trade is placed</li>
          <li>Trades are recorded in the subledger for P&amp;L tracking</li>
        </ul>

        <p><strong>When OFF:</strong></p>
        <ul>
          <li>Your dealing plan is still built at 08:40 and sent to you on WhatsApp with a link, but no orders are placed for any fund</li>
          <li>The signal is still displayed on the Signal tab for reference</li>
          <li>Any trades are placed by you directly in HL, before the 09:00 dealing cutoff</li>
        </ul>

        <p>Each fund's outcome for the day is shown leg by leg in the <strong>Dealing Plan</strong> above. If the system was down during the morning dealing window, no trade is placed &mdash; it never trades outside the window to avoid stale signals.</p>
      </div>
      <div class="toggle-row" v-if="!isViewer">
        <label class="toggle">
          <input type="checkbox" v-model="config.autoExecute" @change="toggleAutoExecute" />
          <span class="toggle-slider"></span>
        </label>
        <span>{{ config.autoExecute ? 'ON' : 'OFF' }}</span>
        <span v-if="saved" class="saved-badge">Saved</span>
      </div>
      <div v-else class="viewer-note" data-testid="viewer-readonly-note">Read-only account</div>
      <div v-if="configLoaded && !config.autoExecute" class="viewer-note" data-testid="auto-execute-off-note">Nothing is traded for you. Your dealing plan is still built at 08:40 and sent to you on WhatsApp — deal it yourself in HL before 09:00.</div>

      <div class="exec-row" data-testid="auto-execute-readonly" v-if="isViewer">
        <label class="exec-label">Auto-Execute</label>
        <span>{{ config.autoExecute ? 'ON' : 'OFF' }}</span>
      </div>

      <!-- OTP sleeve — residual-funded (OTP/BGA divorce Phase 1, Task 4):
           read-only. Replaces the retired OTP Reserve/Account dropdowns —
           OTP sizing now comes from post-cutoff residual cash, not an
           operator-set reserve/account pair. -->
      <div class="exec-row" data-testid="otp-residual-row" style="margin-top: 8px; border-top: 1px solid #222; padding-top: 8px;">
        <label class="exec-label">OTP sleeve — residual-funded</label>
        <span class="otp-residual-value">{{ otpResidualLine }}</span>
      </div>
      <div class="exec-row" data-testid="auto-compound-readonly">
        <label class="exec-label">Auto-compound</label>
        <span class="compound-hint">{{ config.compoundOtp ? 'On' : 'Off' }} — set on the Account page</span>
      </div>
    </div>

    <!-- Signal at Decision Time — one row per accessible fund (2026-10-02).
         Each fund's signal is its own metric: BGA/JH synth-NAV move (%),
         Gold/GGE 08:40 futures move (bp), Japan 08:40 m vs its threshold. -->
    <div class="card" data-testid="decisions-card">
      <h3>Signal at Decision Time</h3>
      <div class="decision-head">
        <span>Fund</span><span>Decision</span><span class="num">Signal</span><span class="num">Time</span>
      </div>
      <div v-for="d in decisions" :key="d.fund" class="decision-row" :data-testid="'decision-' + d.fund">
        <span class="fund-name">{{ FUND_LABEL[d.fund] }}</span>
        <span :class="d.decision ? 'decision-' + d.decision.toLowerCase() : 'muted'">{{ d.decision ?? '—' }}</span>
        <span class="num" :class="{ positive: (d.signalValue ?? 0) > 0, negative: (d.signalValue ?? 0) < 0 }">{{ formatSignal(d) }}</span>
        <span class="num muted">{{ formatTime(d.decidedAtUtc) }}</span>
      </div>
      <div v-if="decisions.length === 0" class="muted">No decisions yet today.</div>
    </div>

    <!-- The former "OTP Instruments" section (Shadow OTP card + active
         Hedging toggle + decommissioned Long Conviction / Long Overlay /
         Short Conviction card) was moved to OtpWindowPanel on the otp
         dashboard (otp.buxushedgefund.com). Fund dashboards' Exec tabs no
         longer host OTP controls — see the 2026-09-06 OTP shadow-promotion
         arc plan for context. -->

    <div v-if="error" class="error-box">{{ error }}</div>
  </section>
</template>

<style scoped>
.exec-panel { display: flex; flex-direction: column; gap: 12px; }
.card { border: 1px solid #333; border-radius: 4px; padding: 1rem; }
.card h3 { margin: 0 0 0.75rem; font-size: 0.9rem; color: #888; text-transform: uppercase; letter-spacing: 0.05em; }

.info-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 16px; height: 16px; border-radius: 50%;
  background: #21262d; color: #8b949e; font-size: 0.6rem;
  cursor: pointer; font-style: italic; font-weight: 600;
  margin-left: 4px; vertical-align: middle;
}
.info-icon:hover { background: #30363d; color: #c9d1d9; }

.info-panel {
  background: #0d1117;
  border: 1px solid #21262d;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  margin-bottom: 0.75rem;
  font-size: 0.82rem;
  color: #8b949e;
  line-height: 1.6;
}
.info-panel p { margin: 0 0 0.5rem 0; }
.info-panel strong { color: #c9d1d9; }
.info-panel ul, .info-panel ol { margin: 0 0 0.5rem 1.2rem; padding: 0; }
.info-panel li { margin-bottom: 0.25rem; }

/* Toggle switch */
.toggle-row { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; }
.toggle { position: relative; display: inline-block; width: 50px; height: 28px; }
.toggle input { opacity: 0; width: 0; height: 0; }
.toggle-slider {
  position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0;
  background: #444; border-radius: 28px; transition: 0.3s;
}
.toggle-slider:before {
  content: ""; position: absolute; height: 22px; width: 22px; left: 3px; bottom: 3px;
  background: #ccc; border-radius: 50%; transition: 0.3s;
}
.toggle input:checked + .toggle-slider { background: #4caf50; }
.toggle input:checked + .toggle-slider:before { transform: translateX(22px); }

/* OTP exec rows */
.exec-row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.exec-label { color: #aaa; font-size: 13px; min-width: 90px; }
.exec-select { padding: 8px; background: #222; color: #ccc; border: 1px solid #444; border-radius: 4px; font-size: 13px; min-height: 44px; flex: 1; }
.compound-hint { font-size: 0.75rem; color: #6e7681; }
.otp-residual-value { color: #8bb; font-size: 13px; font-family: 'JetBrains Mono', monospace; flex: 1; }

/* Signal at Decision Time — per-fund rows; fixed narrow columns so the
   table fits a phone without horizontal scroll. */
.decision-head, .decision-row {
  display: grid; grid-template-columns: 3.5rem 4.25rem 1fr 3rem; gap: 8px;
  align-items: center; padding: 6px 0; font-size: 13px;
}
.decision-head { color: #6e7681; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #2a2a2a; }
.decision-row { border-bottom: 1px solid #2a2a2a; }
.decision-row .num, .decision-head .num { text-align: right; font-family: 'JetBrains Mono', monospace; }
.fund-name { color: #c9d1d9; }
.muted { color: #6e7681; }
.positive { color: #4caf50; }
.negative { color: #f44336; }
.decision-buy { color: #4caf50; font-weight: bold; }
.decision-sell { color: #f44336; font-weight: bold; }
.decision-hold { color: #ff9800; }

.saved-badge { font-size: 11px; color: #4caf50; padding: 2px 8px; background: #1b3a1b; border-radius: 3px; animation: fade-in 0.2s; }
.viewer-note { color: #8b949e; font-size: 13px; font-style: italic; margin-bottom: 8px; }
@keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }

.error-box { padding: 10px; background: #3a1b1b; border: 1px solid #f44336; border-radius: 4px; color: #f44336; font-size: 13px; }

/* OTP Instrument Switches */
.section-header { font-size: 0.75rem; color: #8b949e; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.5rem; }
.otp-switches { display: flex; flex-direction: column; gap: 0.5rem; }
.otp-row { display: flex; flex-direction: column; padding: 0.5rem; background: #161b22; border-radius: 6px; gap: 0.4rem; }
.otp-main-row { display: flex; justify-content: space-between; align-items: center; }
.otp-info { display: flex; flex-direction: column; gap: 2px; }
.otp-label { font-size: 0.85rem; color: #e6edf3; }
.otp-inst { font-size: 0.75rem; color: #6e7681; font-family: 'JetBrains Mono', monospace; }
.otp-desc { font-size: 0.65rem; color: #6e7681; }
.otp-threshold { font-size: 0.7rem; color: #8b949e; font-family: 'JetBrains Mono', monospace; }
.otp-sub-row { display: flex; align-items: center; gap: 0.5rem; padding-left: 0.25rem; }
.otp-sub-label { font-size: 0.75rem; color: #888; min-width: 70px; }
.otp-sub-select { width: 80px; flex: none; min-height: 32px; padding: 4px 6px; font-size: 0.75rem; }

/* Decommissioned strategies banner + collapsible — Phase B (2026-09-06). */
.decomm-banner {
  background: #2a2210; border: 1px solid #6b5a1f; border-radius: 4px;
  padding: 0.6rem 0.75rem; margin-bottom: 0.75rem;
  font-size: 0.8rem; color: #e0c86b; line-height: 1.45;
}
.decomm-banner code {
  background: #1a1a1a; padding: 1px 4px; border-radius: 3px;
  font-size: 0.75rem; color: #eec;
}
.decomm-collapsible {
  background: #14181c; border: 1px solid #2a2f36; border-radius: 4px;
  padding: 0.4rem 0.6rem; margin: 0.6rem 0 0;
}
.decomm-collapsible > summary {
  cursor: pointer; user-select: none; font-size: 0.8rem;
  color: #b8a768; letter-spacing: 0.5px; padding: 0.15rem 0;
}
.decomm-collapsible[open] > summary { border-bottom: 1px solid #333; margin-bottom: 0.5rem; padding-bottom: 0.35rem; }
.decomm-fieldset { border: 1px dashed #3a3a3a; opacity: 0.7; padding: 0.5rem; }
.decomm-fieldset[disabled] { pointer-events: none; }
.decomm-save-btn {
  margin-top: 0.5rem; padding: 6px 12px; border-radius: 3px;
  background: #222; border: 1px solid #444; color: #888;
  font-size: 0.78rem; cursor: not-allowed; opacity: 0.6;
}

/* Toggle switch for OTP rows */
.toggle-switch { position: relative; display: inline-block; width: 40px; height: 22px; flex-shrink: 0; }
.toggle-switch input { opacity: 0; width: 0; height: 0; }
.slider {
  position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0;
  background: #444; border-radius: 22px; transition: 0.3s;
}
.slider:before {
  content: ""; position: absolute; height: 16px; width: 16px; left: 3px; bottom: 3px;
  background: #ccc; border-radius: 50%; transition: 0.3s;
}
.toggle-switch input:checked + .slider { background: #4caf50; }
.toggle-switch input:checked + .slider:before { transform: translateX(18px); }
</style>
